/**
 * Componente: Dashboard Principal
 * Diseño: Gradiente Turquesa-Púrpura Premium
 * Ubicación: src/pages/Dashboard.jsx
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Padron from '../components/Padron';
import Personal from '../components/Personal';
import Disenador from '../components/Disenador';
import Auditoria from '../components/Auditoria';
import Reportes from '../components/Reportes';
import NotificacionesBell from '../components/NotificacionesBell';
import {
  InsigniaIcono,
  IconDashboard,
  IconPersonal,
  IconPadron,
  IconDisenador,
  IconReportes,
  IconAuditoria,
  IconLicencia,
  IconExito,
  IconPendiente,
  IconAgregar,
  IconPersona,
  IconAdvertencia,
} from '../components/icons';
import { iniciarVigilanteSesion } from '../utils/vigilanteSesion';
import { cerrarCookieArchivos } from '../utils/sesionArchivos';
import '../styles/dashboard.css';
import '../styles/disenador.css';

const API_URL = process.env.REACT_APP_API_URL;

const MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: IconDashboard, roles: ['admin'] },
  { id: 'personal', label: 'Personal', icon: IconPersonal, roles: ['admin'] },
  { id: 'padron', label: 'Padrón', icon: IconPadron, roles: ['admin', 'operador'] },
  { id: 'diseñador', label: 'Diseñador', icon: IconDisenador, roles: ['admin'] },
  { id: 'reportes', label: 'Reportes', icon: IconReportes, roles: ['admin'] },
  { id: 'auditoria', label: 'Auditoría', icon: IconAuditoria, roles: ['admin'] },
];

// No hay una ruta por sección (todo vive en seccionActiva dentro de este
// componente), así que el control de acceso real pasa por acá: aunque
// alguien fuerce seccionActiva a un id restringido, el contenido no se
// renderiza si el rol del usuario no está en la whitelist de ese ítem.
const puedeVerSeccion = (id, rol) => MENU_ITEMS.find((item) => item.id === id)?.roles.includes(rol) ?? false;

const Dashboard = () => {
  const [usuario, setUsuario] = useState(null);
  const [municipio, setMunicipio] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    totalPersonal: 0,
    licenciasGeneradas: 0,
    licenciasImpresas: 0,
    licenciasPendientes: 0,
  });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [seccionActiva, setSeccionActiva] = useState('dashboard');
  // Se incrementa en cada click al ítem "Diseñador" del sidebar (incluso si
  // ya estaba activo) y se le pasa a <Disenador> como prop `resetSignal`.
  // Disenador.jsx tiene un useEffect que escucha este valor y fuerza
  // pantalla='galeria' explícitamente. Clickear un ítem ya activo no
  // cambia seccionActiva (React no re-renderiza ese estado si el valor es
  // igual), así que hace falta esta señal aparte para que el componente
  // sepa que "volvé a la galería" incluso sin cambiar de sección.
  const [disenadorResetKey, setDisenadorResetKey] = useState(0);

  // Cargar datos al montar
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setCargando(true);
      setError('');

      // Obtener usuario del sessionStorage
      const usuarioGuardado = sessionStorage.getItem('usuario');
      if (usuarioGuardado) {
        const usuarioParseado = JSON.parse(usuarioGuardado);
        setUsuario(usuarioParseado);
        // Operador no ve estadísticas de admin: aterriza directo en Padrón
        setSeccionActiva(usuarioParseado.rol === 'operador' ? 'padron' : 'dashboard');
      }

      const token = sessionStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/dashboard/resumen`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEstadisticas({
        totalPersonal: data.personal,
        licenciasGeneradas: data.licencias_generadas_mes,
        licenciasImpresas: data.licencias_impresas,
        licenciasPendientes: data.licencias_pendientes,
      });
      setMunicipio(data.municipio);
    } catch (err) {
      setError('Error al cargar datos');
      console.error(err);
    } finally {
      setCargando(false);
    }
  };

  // Solo admin tiene Dashboard/otros módulos a los que volver — operador
  // tiene Padrón como única pantalla, así que nunca se le pasa esta función.
  const volverADashboard = () => setSeccionActiva('dashboard');

  const handleLogout = async () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('usuario');
    // Además del token, el servidor borra la cookie de los archivos subidos (HttpOnly: la página no puede borrarla sola).
    await cerrarCookieArchivos();
    window.location.href = '/login';
  };

  // --- Vigilante de la sesión: detecta una cuenta desactivada sin esperar a que la pantalla pida algo ---
  // (ver utils/vigilanteSesion.js). El corte y el cartel los hace el interceptor de axios.
  useEffect(() => iniciarVigilanteSesion({
    ping: () => axios.get(`${API_URL}/auth/sesion`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    }),
  }), []);

  // --- Cierre automático por inactividad nocturna ---
  // Solo aplica a la sesión de un municipio: este componente nunca se
  // monta para Superadmin (pantalla totalmente aparte, ver Superadmin.jsx
  // y App.js). Pensado para una PC compartida de oficina: si la app queda
  // abierta de noche (21:00 a 06:00) y nadie interactúa durante 15
  // minutos seguidos, cierra la sesión sola. Fuera de esa franja horaria
  // la inactividad no hace nada — de día puede haber pausas largas
  // legítimas sin que signifiquen "se olvidaron la sesión abierta".
  useEffect(() => {
    const MINUTOS_INACTIVIDAD = 15;
    const HORA_INICIO_NOCTURNA = 21; // 21:00, inclusive
    const HORA_FIN_NOCTURNA = 6; // 06:00, exclusive
    const INTERVALO_CHEQUEO_MS = 60 * 1000; // 1 minuto de sobra para un corte de 15

    const esHorarioNocturno = () => {
      const hora = new Date().getHours();
      return hora >= HORA_INICIO_NOCTURNA || hora < HORA_FIN_NOCTURNA;
    };

    // Se guarda en una variable de closure (no en un estado de React) a
    // propósito: cada click/tecla/scroll reiniciaría un render si fuera
    // useState, sin ningún beneficio — acá solo hace falta el timestamp
    // para el chequeo periódico de abajo.
    let ultimaInteraccion = Date.now();
    const registrarInteraccion = () => { ultimaInteraccion = Date.now(); };

    const eventos = ['click', 'keydown', 'scroll'];
    eventos.forEach((ev) => window.addEventListener(ev, registrarInteraccion, { passive: true }));

    const intervalo = setInterval(() => {
      const minutosInactivo = (Date.now() - ultimaInteraccion) / 60000;
      if (esHorarioNocturno() && minutosInactivo >= MINUTOS_INACTIVIDAD) {
        handleLogout();
      }
    }, INTERVALO_CHEQUEO_MS);

    return () => {
      eventos.forEach((ev) => window.removeEventListener(ev, registrarInteraccion));
      clearInterval(intervalo);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (cargando) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* NAVBAR */}
      <nav className="dashboard-navbar">
        <div className="navbar-left">
          <div className="navbar-logo">
            <span className="logo-text">Sistema de Gestión de Licencias de Conducir</span>
          </div>
        </div>

        <div className="navbar-center">
          <span className="navbar-title">
            {municipio ? municipio.nombre.toUpperCase() : 'SISTEMA'}
          </span>
        </div>

        <div className="navbar-right">
          <NotificacionesBell
            esAdmin={usuario?.rol === 'admin'}
            onIrAAuditoria={() => setSeccionActiva('auditoria')}
          />
          <div className="navbar-user">
            <span className="user-avatar"><IconPersona size={20} /></span>
            <span className="user-name">
              {usuario?.nombre || 'Admin'}
            </span>
            <button className="btn-logout" onClick={handleLogout}>
              Salir
            </button>
          </div>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <div className="dashboard-main">
        {/* SIDEBAR */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-menu">
            {MENU_ITEMS.filter((item) => item.roles.includes(usuario?.rol || 'admin')).map((item) => {
              const Icono = item.icon;
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`menu-item ${seccionActiva === item.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSeccionActiva(item.id);
                    if (item.id === 'diseñador') setDisenadorResetKey((k) => k + 1);
                  }}
                >
                  <InsigniaIcono size={30}>
                    <Icono size={17} />
                  </InsigniaIcono>
                  <span className="menu-text">{item.label}</span>
                </a>
              );
            })}
          </div>

          <div className="sidebar-footer">
            <div className="sidebar-info">
              <p className="sidebar-version">v1.0.0</p>
              <p className="sidebar-copyright">© 2026 Betech</p>
            </div>
          </div>
        </aside>

        {/* CONTENT */}
        <main className="dashboard-content">
          {/* HEADER */}
          <div className="content-header">
            <h1>Bienvenido, {usuario?.nombre || 'Admin'}</h1>
            <p className="content-subtitle">
              Municipio: <strong>{municipio?.nombre || 'tumunicipio'}</strong>
            </p>
          </div>

          {/* ERROR MESSAGE */}
          {error && (
            <div className="alert alert-error">
              <IconAdvertencia size={18} />
              <p>{error}</p>
            </div>
          )}

          {seccionActiva === 'dashboard' && puedeVerSeccion('dashboard', usuario?.rol) && (
            <>
              {/* ESTADÍSTICAS */}
              <section className="dashboard-stats">
                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconPersonal size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Personal</h3>
                    <div className="stat-number">{estadisticas.totalPersonal}</div>
                    <p className="stat-label">Total registrado</p>
                  </div>
                </div>

                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconLicencia size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Licencias Generadas</h3>
                    <div className="stat-number">{estadisticas.licenciasGeneradas}</div>
                    <p className="stat-label">En este mes</p>
                  </div>
                </div>

                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconExito size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Licencias Impresas</h3>
                    <div className="stat-number">{estadisticas.licenciasImpresas}</div>
                    <p className="stat-label">Exitosas</p>
                  </div>
                </div>

                <div className="stat-card">
                  <InsigniaIcono size={64}>
                    <IconPendiente size={30} />
                  </InsigniaIcono>
                  <div className="stat-content">
                    <h3>Pendientes</h3>
                    <div className="stat-number">{estadisticas.licenciasPendientes}</div>
                    <p className="stat-label">En cola</p>
                  </div>
                </div>
              </section>

              {/* QUICK ACTIONS */}
              <section className="dashboard-actions">
                <h2>Acciones Rápidas</h2>
                <div className="actions-grid">
                  <button className="action-btn" onClick={() => setSeccionActiva('personal')}>
                    <InsigniaIcono size={46}>
                      <IconAgregar size={22} />
                    </InsigniaIcono>
                    <span className="action-text">Agregar Personal</span>
                  </button>
                  <button className="action-btn" onClick={() => setSeccionActiva('diseñador')}>
                    <InsigniaIcono size={46}>
                      <IconDisenador size={22} />
                    </InsigniaIcono>
                    <span className="action-text">Diseñar Licencia</span>
                  </button>
                  <button className="action-btn" onClick={() => setSeccionActiva('reportes')}>
                    <InsigniaIcono size={46}>
                      <IconReportes size={22} />
                    </InsigniaIcono>
                    <span className="action-text">Ver Reportes</span>
                  </button>
                </div>
              </section>

              {/* INFORMACIÓN DEL MUNICIPIO */}
              <section className="dashboard-info">
                <h2>Información del Municipio</h2>
                <div className="info-card">
                  <div className="info-row">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">{municipio?.nombre}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Provincia:</span>
                    <span className="info-value">{municipio?.provincia}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Contacto:</span>
                    <span className="info-value">{municipio?.contacto}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Estado:</span>
                    <span className="info-value status-active"><IconExito size={16} /> Activo</span>
                  </div>
                </div>
              </section>
            </>
          )}

          {seccionActiva === 'personal' && puedeVerSeccion('personal', usuario?.rol) && (
            <Personal onVolver={volverADashboard} />
          )}

          {seccionActiva === 'padron' && puedeVerSeccion('padron', usuario?.rol) && (
            <Padron onVolver={usuario?.rol === 'admin' ? volverADashboard : null} esAdmin={usuario?.rol === 'admin'} />
          )}

          {seccionActiva === 'diseñador' && puedeVerSeccion('diseñador', usuario?.rol) && (
            <Disenador resetSignal={disenadorResetKey} onVolverDashboard={volverADashboard} />
          )}

          {seccionActiva === 'reportes' && puedeVerSeccion('reportes', usuario?.rol) && (
            <Reportes onVolver={volverADashboard} />
          )}

          {seccionActiva === 'auditoria' && puedeVerSeccion('auditoria', usuario?.rol) && (
            <Auditoria onVolver={volverADashboard} esAdmin={usuario?.rol === 'admin'} />
          )}

          {/* FOOTER */}
          <footer className="dashboard-footer">
            <p>
              Sistema de Gestión de Licencias PVC © 2026 Betech Solutions |
              <a href="#">Política de Privacidad</a> |
              <a href="#">Términos de Uso</a>
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
